import { Alert, Box, Button, Grid, Skeleton, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobals } from 'src/shared/contexts/appContext';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAssociationById, updateAssociation, uploadAssociationLogo, deleteAssociationLogo } from 'src/shared/services/associationService';
import { AssociationResponse } from 'src/modules/associations/models/associationResponse';
import { AssociationRequest } from 'src/modules/associations/models/associationRequest';
import { ImageCropperModal } from 'src/shared/components/organisms/imageCropperModal';
import { dataUrlToFile } from 'src/shared/functions/dataUrlToFile';
import { FormProvider, useForm } from 'react-hook-form';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import AxiosInstance from 'src/shared/services/axios/interceptor';
import { toast } from 'react-toastify';

type OnboardingStepOrganizationProps = {
  onLogoChange?: (hasLogo: boolean) => void;
};

export const OnboardingStepOrganization = ({ onLogoChange }: OnboardingStepOrganizationProps) => {
  const { t } = useTranslation(['onboarding', 'common', 'association']);
  const { currentUser } = useGlobals();
  const isMounted = useIsMounted();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Cropper
  const [cropperOpen, setCropperOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const localAssId = currentUser?.associations?.[0]?.localAss_id;
  const isFederation = (currentUser?.associationCount ?? 0) > 1 || (currentUser?.districtCount ?? 0) > 0;

  const formMethods = useForm<AssociationRequest>({
    mode: 'all',
    defaultValues: {
      localAss: '',
      nr: '',
      shortName: '',
      cOaddress: '',
      address: '',
      addressLocal: '',
      postNr: '',
      postAddress: '',
      homesite: '',
      phone: '',
      phoneOther: '',
      email: '',
      emailResponsible: '',
      fee: '',
      bankGiro: '',
      postGiro: '',
      orgNr: '',
      customerNr: '',
      comment: '',
    },
  });

  const { reset } = formMethods;

  const fetchData = useCallback(async () => {
    if (!localAssId) return;
    setLoading(true);
    try {
      const res = await getAssociationById(`${localAssId}`);
      if (isMounted()) {
        reset(res.data);
        if (res.data.logoImage_id > 0) {
          try {
            const logoRes = await AxiosInstance.get(`Association/${localAssId}/Logo`, { responseType: 'blob' });
            const blobUrl = URL.createObjectURL(logoRes.data);
            if (isMounted()) {
              setLogoUrl(blobUrl);
              onLogoChange?.(true);
            }
          } catch {
            // Ingen logga finns
          }
        }
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [localAssId, isMounted, reset]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedImage(URL.createObjectURL(file));
    setCropperOpen(true);
    e.target.value = '';
  };

  const handleCropComplete = async (croppedDataUrlOrPromise: string | Promise<string | null> | null) => {
    setCropperOpen(false);
    const croppedDataUrl = croppedDataUrlOrPromise instanceof Promise ? await croppedDataUrlOrPromise : croppedDataUrlOrPromise;
    if (!croppedDataUrl || !localAssId) return;

    const file = dataUrlToFile(croppedDataUrl, 'logo.png');
    if (!file) return;

    setUploading(true);
    try {
      await uploadAssociationLogo(localAssId, file);
      const logoRes = await AxiosInstance.get(`Association/${localAssId}/Logo`, { responseType: 'blob' });
      setLogoUrl(URL.createObjectURL(logoRes.data));
      onLogoChange?.(true);
      toast.success(t('onboarding:stepContent.organization.logoUploaded') as string);
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(t('common:error.general') as string);
    } finally {
      if (isMounted()) setUploading(false);
    }
  };

  const handleSave = async (data: AssociationRequest) => {
    setSaving(true);
    try {
      await updateAssociation(data);
      toast.success(t('association:saveSuccess') as string);
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(t('common:error.general') as string);
    } finally {
      if (isMounted()) setSaving(false);
    }
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Box sx={{ p: 2, position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
            <Typography variant='body1' sx={{ mb: 2 }}>
              {t('onboarding:stepContent.organization.description')}
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              {t('onboarding:stepContent.organization.hint')}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={8}>
          {isFederation ? (
            <Alert severity='warning'>{t('onboarding:stepContent.organization.federationHint')}</Alert>
          ) : (
            <>
              <Typography variant='h6' fontWeight={600} sx={{ mb: 1 }}>
                {t('onboarding:stepContent.organization.infoTitle')}
              </Typography>
              <Box sx={{ bgcolor: '#fff', borderRadius: 1, p: 2, border: 1, borderColor: 'grey.300', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }}>
                <Alert severity='info' sx={{ mb: 2 }}>
                  {t('onboarding:stepContent.organization.usageHint')}
                </Alert>
                {loading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Skeleton variant='rounded' height={65} width={180} />
                    <Skeleton variant='rounded' height={40} />
                    <Skeleton variant='rounded' height={40} />
                  </Box>
                ) : (
                  <FormProvider {...formMethods}>
                    <Grid container spacing={3}>
                      {/* Logotyp */}
                      <Grid item xs={12} md={4}>
                        <Typography variant='subtitle2' fontWeight={600} sx={{ mb: 1 }}>
                          {t('onboarding:stepContent.organization.logoLabel')}
                        </Typography>
                        <Box
                          sx={{
                            width: 180,
                            height: 65,
                            border: 2,
                            borderStyle: logoUrl ? 'solid' : 'dashed',
                            borderColor: 'grey.300',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.50',
                            overflow: 'hidden',
                            mb: 1.5,
                          }}
                        >
                          {logoUrl ? (
                            <Box component='img' src={logoUrl} alt='Logo' sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 1 }} />
                          ) : (
                            <CloudUploadIcon sx={{ fontSize: 36, color: 'grey.400' }} />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant='outlined'
                            size='small'
                            startIcon={<CloudUploadIcon />}
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                          >
                            {logoUrl ? t('onboarding:stepContent.organization.changeLogo') : t('onboarding:stepContent.organization.uploadLogo')}
                          </Button>
                          {logoUrl && (
                            <Button
                              variant='outlined'
                              size='small'
                              color='error'
                              startIcon={<DeleteIcon />}
                              onClick={async () => {
                                if (!localAssId) return;
                                try {
                                  await deleteAssociationLogo(localAssId);
                                  setLogoUrl(null);
                                  onLogoChange?.(false);
                                } catch (e) {
                                  ConsoleHelper.log(e);
                                }
                              }}
                              disabled={uploading}
                            >
                              {t('common:button.delete')}
                            </Button>
                          )}
                        </Box>
                        <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, lineHeight: 1.3, display: 'block' }}>
                          {t('onboarding:stepContent.organization.logoHint')}
                        </Typography>
                        <input ref={fileInputRef} type='file' accept='image/png,image/jpeg' style={{ display: 'none' }} onChange={handleFileSelect} />
                      </Grid>

                      {/* Redigerbara fält */}
                      <Grid item xs={12} md={8}>
                        <Typography variant='subtitle2' fontWeight={600} sx={{ mb: 1 }}>
                          {t('onboarding:stepContent.organization.associationInfo')}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <ControlledTextField label={t('association:address')} name='address' />
                          </Grid>
                          <Grid item xs={6}>
                            <ControlledTextField label={t('association:coAddress')} name='cOaddress' />
                          </Grid>
                          <Grid item xs={4}>
                            <ControlledTextField label={t('association:postNr')} name='postNr' />
                          </Grid>
                          <Grid item xs={8}>
                            <ControlledTextField label={t('association:postAddress')} name='postAddress' />
                          </Grid>
                          <Grid item xs={6}>
                            <ControlledTextField label={t('association:email')} name='email' />
                          </Grid>
                          <Grid item xs={6}>
                            <ControlledTextField label={t('association:phone')} name='phone' />
                          </Grid>
                          <Grid item xs={12}>
                            <ControlledTextField label={t('association:homesite')} name='homesite' />
                          </Grid>
                          <Grid item xs={4}>
                            <ControlledTextField label={t('association:postGiro')} name='postGiro' />
                          </Grid>
                          <Grid item xs={4}>
                            <ControlledTextField label={t('association:bankGiro')} name='bankGiro' />
                          </Grid>
                          <Grid item xs={4}>
                            <ControlledTextField label={t('association:orgNr')} name='orgNr' />
                          </Grid>
                          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button variant='contained' disableElevation disabled={saving} onClick={formMethods.handleSubmit(handleSave)}>
                              {t('common:button.save')}
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </FormProvider>
                )}
              </Box>
            </>
          )}
        </Grid>
      </Grid>

      {/* Cropper-modal */}
      {selectedImage && (
        <ImageCropperModal
          open={cropperOpen}
          image={selectedImage}
          onComplete={handleCropComplete}
          onAbort={() => setCropperOpen(false)}
          containerStyle={{}}
          outputFormat='image/png'
          aspect={3 / 1}
        />
      )}
    </Box>
  );
};
